import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Sparkles,
  ArrowRight,
  X,
  Bot,
  User,
  Zap,
  AlertCircle,
  Plus,
  CheckCircle,
  ClipboardCheck,
  RotateCcw,
  Copy,
  ChevronRight,
  Info,
  Search,
  Clock,
  TrendingUp,
  BarChart3,
  Download
} from 'lucide-react';
import classes from './AICommandBar.module.css';
import { Button } from '../common/Button';
import { AIAssistantService, ReportingService, ExportService, ComplianceService } from '../../services/api';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAIConversation } from '../../contexts/AIConversationContext';
import { AI_SUGGESTIONS } from '../../config/aiSuggestions';
import { parsePolicyCommand, POLICY_INTENTS, FULL_ACCESS_INTENTS } from '../../lib/policy-commandbar-layer1';
import { useToast } from '../../contexts/ToastContext';
import { useAssessmentAI } from '../../contexts/AssessmentAIContext';
import { shouldEscalateSearchToAI } from '../../utils/commandSearch';
import { buildContextualFollowUps, normalizeAssistantMarkdown } from '../../utils/aiResponse';

/* ─── Component ───────────────────────────────────────────── */
export const AICommandBar = ({ onSearch, onDockToHelp }) => {
    const inputRef = useRef(null);
    const barRef = useRef(null);
    const panelRef = useRef(null);
    const suggestionRef = useRef(null);
    const messagesEndRef = useRef(null);

    const [value, setValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const {
        messages,
        addMessage,
        isLoading,
        setIsLoading
    } = useAIConversation();
    const { showToast } = useToast();
    const { assessmentContext } = useAssessmentAI();

    const [followUp, setFollowUp] = useState('');
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
    const [recentQueries, setRecentQueries] = useState([]);

    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const location = useLocation();
    const navigate = useNavigate();

    // Simplified admin check for CS Comply. Default to false if roles not clear.
    const isAdmin = activeAccount?.idTokenClaims?.roles?.some(role =>
        ['SuperAdmin', 'ConsultantAdmin', 'OrgAdmin', 'Admin', 'Assessor', 'Implementer'].includes(role)
    ) || false;

    const logRouting = useCallback((outcome, details = {}) => {
        console.info(`[AI Command Bar][Routing] ${outcome}`, details);
    }, []);

    /* ─── Load History ────────────────────────────────────────── */
    useEffect(() => {
        const history = localStorage.getItem('ai_query_history');
        if (history) {
            try {
                setRecentQueries(JSON.parse(history));
            } catch (e) {
                console.error('Failed to parse history', e);
            }
        }
    }, []);

    const saveQueryToHistory = (query) => {
        if (!query.trim()) return;
        setRecentQueries(prev => {
            const next = [query, ...prev.filter(q => q !== query)].slice(0, 5);
            localStorage.setItem('ai_query_history', JSON.stringify(next));
            return next;
        });
    };

    /* Show suggestion panel: focused, bar empty or partial text, no open conversation */
    const showSuggestions = isFocused && !isOpen;

    /* Position the floating panel under the bar */
    const updatePanelPos = useCallback(() => {
        if (!barRef.current) return;
        const rect = barRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const targetWidth = Math.min(vw * 0.70, 1000); // ~70vw, never more than 1000px
        const left = Math.max((vw - targetWidth) / 2, 16); // center horizontally
        setPanelPos({
            top: rect.bottom + 12,
            left,
            width: targetWidth,
        });
    }, []);

    useEffect(() => {
        if (isOpen || isFocused) {
            updatePanelPos();
            window.addEventListener('resize', updatePanelPos);
            window.addEventListener('scroll', updatePanelPos, true);
        }
        return () => {
            window.removeEventListener('resize', updatePanelPos);
            window.removeEventListener('scroll', updatePanelPos, true);
        };
    }, [isOpen, isFocused, updatePanelPos]);

    /* Global ⌘K shortcut + custom event from HelpPanel "AI Support" tab */
    useEffect(() => {
        const handleKeyDownGlobal = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
            }
            if (e.key === 'Escape' && isOpen) {
                closePanel();
            }
        };

        const handleOpenEvent = () => {
            updatePanelPos();
            if (messages.length > 0) {
                setIsOpen(true);
            } else {
                inputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDownGlobal);
        window.addEventListener('ai-command-bar:focus', handleOpenEvent);
        return () => {
            window.removeEventListener('keydown', handleKeyDownGlobal);
            window.removeEventListener('ai-command-bar:focus', handleOpenEvent);
        };
    }, [isOpen, messages.length, updatePanelPos]);

    /* Close on outside click */
    useEffect(() => {
        const handleClickOutside = (e) => {
            const isInside =
                barRef.current?.contains(e.target) ||
                panelRef.current?.contains(e.target) ||
                suggestionRef.current?.contains(e.target);

            if (!isInside) {
                closePanel();
            }
        };
        if (isOpen || isFocused) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, isFocused]);

    const closePanel = () => {
        setIsOpen(false);
        setIsFocused(false);
        inputRef.current?.blur();
    };

    const handleSuggestionClick = (suggestion) => {
        // Populate the bar and keep focus for additional input as requested
        const newVal = suggestion + ' ';
        setValue(newVal);
        setIsFocused(true);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                const len = newVal.length;
                inputRef.current.setSelectionRange(len, len);
            }
        }, 10);
    };

    /* Scroll to the latest message when it's added or when the answer arrives */
    useEffect(() => {
        if (isOpen && messagesEndRef.current) {
            const timer = setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [messages.length, isOpen, isLoading]);

    const callAI = async (queryValue) => {
        if (!queryValue.trim() || isLoading) return;

        const userMsg = {
            id: Date.now().toString(),
            text: queryValue,
            sender: 'user',
            timestamp: new Date(),
        };
        addMessage(userMsg);
        setValue('');
        setIsLoading(true);
        setIsOpen(true); // Open panel to show conversation progress

        try {
            const conversationHistory = messages.slice(-10).map(message => ({
                role: message.sender === 'bot' ? 'assistant' : 'user',
                content: message.text
            }));

            logRouting('ESCALATED_TO_AI_API', {
                handler: 'ai_api',
                query: queryValue,
                route: location.pathname,
                framework: assessmentContext.framework?.name || null,
                domain: assessmentContext.domain?.name || null,
                control: assessmentContext.control?.id || null,
                question_count: assessmentContext.questions?.length || 0,
            });

            const response = await AIAssistantService.chat({
                message: queryValue,
                context: assessmentContext,
                conversation_history: conversationHistory
            });
            logRouting('AI_API_RESPONSE_RECEIVED', {
                query: queryValue,
                status: response.status,
            });

            const copilotData = response.data;
            const suggestedQuickAsks = copilotData.suggested_quick_asks?.length
                ? copilotData.suggested_quick_asks
                : buildContextualFollowUps(queryValue, assessmentContext);
            const isDegraded = copilotData.intent === 'ERROR' ||
                (copilotData.confidence === 'Low' && !copilotData.gaps?.length && !copilotData.next_actions?.length);

            const botMsg = {
                id: (Date.now() + 1).toString(),
                // Backend returns `response` field (not `assistant_message` or `answer`)
                text: copilotData.response || copilotData.assistant_message || copilotData.answer || copilotData.message || "I couldn't generate a response. Please try again.",
                sender: 'bot',
                timestamp: new Date(),
                data: {
                    ...copilotData,
                    suggested_quick_asks: suggestedQuickAsks,
                    _degraded: isDegraded
                }
            };
            addMessage(botMsg);
        } catch (error) {
            addMessage({
                id: Date.now().toString(),
                text: "Error processing request.",
                sender: 'bot',
                timestamp: new Date()
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFollowUp = () => {
        if (!followUp.trim()) return;
        const q = followUp.trim();
        setFollowUp('');
        callAI(q);
    };


    const triggerSearch = async () => {
        if (!value.trim()) return;
        const query = value.trim();
        setValue('');
        saveQueryToHistory(query);

        // Try Layer 1 first. If handled, don't fall back to parent onSearch (which opens the sidebar AI)
        if (!(await runLayer1(query))) {
            onSearch?.(query);
            setIsOpen(true);
            updatePanelPos();
            callAI(query);
        }
    };

    const handleKeyDownInput = (e) => {
        if (e.key === 'Enter') triggerSearch();
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const handleDock = () => {
        onDockToHelp?.();
        closePanel();
    };

    const handleNavigate = (path, state) => {
        closePanel();
        navigate(path, { state });
    };

    /**
     * Layer 1 pre-filter: run the deterministic parser BEFORE any LLM call.
     * Returns true if Layer 1 handled it (LLM call skipped).
     * Returns false to escalate to callAI().
     */
    const runLayer1 = useCallback(async (queryValue) => {
        // If it starts with /, we allow all deterministic intents regardless of simple frontend role check.
        // The backend will still enforce actual permissions.
        const isSlashCommand = queryValue.trim().startsWith('/');

        const ctx = {
            current_route: location.pathname,
            org_enabled_frameworks: [],
            allowed_intents: (isAdmin || isSlashCommand) ? FULL_ACCESS_INTENTS : POLICY_INTENTS,
        };

        const result = parsePolicyCommand(queryValue, ctx);
        console.info('[AI Command Bar] Layer 1 decision', {
            decision: result.handoff.decision,
            reason: result.handoff.reason || null,
            intent: result.intent,
            confidence: result.confidence,
            result
        });

        if (/^\/draft\b/i.test(queryValue)) {
            logRouting('ESCALATED_TO_AI_API', {
                handler: 'ai_only_command',
                command: '/draft',
                reason: 'DRAFT_REQUIRES_GENERATION',
                context_control: assessmentContext.control?.id || null
            });
            return false;
        }

        if (result.handoff.decision === 'ESCALATE_TO_LAYER2') {
            logRouting('ESCALATED_TO_AI_API', {
                handler: 'internal_nlp_handoff',
                intent: result.intent,
                reason: result.handoff.reason,
                layer2_input: result.layer2_input
            });
            return false;
        }

        const policyCommandMatch = queryValue.match(/^\/polic(?:y|ies)(?:\s+(.+))?$/i);
        if (policyCommandMatch) {
            const policyQuery = (policyCommandMatch[1] || '').trim();
            logRouting('HANDLED_INTERNAL', {
                handler: 'navigation',
                intent: result.intent,
                target: 'documents',
                query: policyQuery || null,
            });
            navigate(policyQuery ? `/documents?q=${encodeURIComponent(policyQuery)}` : '/documents');
            setIsFocused(false);
            return true;
        }

        // In CS Comply, /search defaults to controls. Prefix with "policies" or
        // "reports" when the user wants a different module.
        const slashSearchMatch = queryValue.match(/^\/search(?:\s+(.+))?$/i);
        if (slashSearchMatch) {
            const searchQ = (slashSearchMatch[1] || '').trim();
            if (!searchQ) {
                logRouting('HANDLED_INTERNAL', {
                    handler: 'command_validation',
                    intent: result.intent,
                    reason: 'MISSING_SEARCH_QUERY',
                });
                addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
                addMessage({
                    id: (Date.now() + 1).toString(),
                    text: 'Enter what you want to find, for example `/search access control` or `/search policies incident response`.',
                    sender: 'bot',
                    timestamp: new Date()
                });
                setIsOpen(true);
                updatePanelPos();
                return true;
            }

            const policyMatch = searchQ.match(/^polic(?:y|ies)\s+(.+)$/i);
            const reportMatch = searchQ.match(/^reports?\s+(.+)$/i);
            if (policyMatch) {
                logRouting('HANDLED_INTERNAL', {
                    handler: 'navigation',
                    intent: 'policy_search',
                    target: 'documents',
                    query: policyMatch[1],
                });
                navigate(`/documents?q=${encodeURIComponent(policyMatch[1])}`);
                setIsFocused(false);
                return true;
            }
            if (reportMatch) {
                logRouting('HANDLED_INTERNAL', {
                    handler: 'navigation',
                    intent: 'report_search',
                    target: 'reports',
                    query: reportMatch[1],
                });
                navigate(`/reports?q=${encodeURIComponent(reportMatch[1])}`);
                setIsFocused(false);
                return true;
            }

            if (shouldEscalateSearchToAI(searchQ)) {
                logRouting('ESCALATED_TO_AI_API', {
                    handler: 'natural_language_search',
                    intent: result.intent,
                    reason: 'QUESTION_LIKE_SEARCH',
                    query: searchQ,
                    context_control: assessmentContext.control?.id || null
                });
                return false;
            }

            try {
                const framework = assessmentContext.framework?.name || null;
                const resp = await ComplianceService.searchControls(searchQ, framework);
                const data = resp.data;

                if (data.count === 0) {
                    logRouting('ESCALATED_TO_AI_API', {
                        handler: 'control_search_fallback',
                        intent: result.intent,
                        reason: 'NO_DETERMINISTIC_RESULTS',
                        query: searchQ,
                        framework
                    });
                    return false;
                }

                addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
                setIsOpen(true);
                updatePanelPos();
                addMessage({
                    id: (Date.now() + 1).toString(),
                    text: `Found **${data.count}** control${data.count !== 1 ? 's' : ''} matching "${searchQ}".`,
                    sender: 'bot',
                    timestamp: new Date(),
                    data: { intent: 'COMPLIANCE_CONTROL', control_data: data }
                });
                logRouting('HANDLED_INTERNAL', {
                    handler: 'compliance_api',
                    intent: 'compliance_control_search',
                    query: searchQ,
                    result_count: data.count,
                });
            } catch (err) {
                console.error('Error searching controls:', err);
                logRouting('ESCALATED_TO_AI_API', {
                    handler: 'control_search_fallback',
                    intent: result.intent,
                    reason: 'DETERMINISTIC_API_ERROR',
                    query: searchQ,
                });
                return false;
            }
            return true;
        }

        // ── compliance_readiness_view (existing) ──────────────────────────────
        if (result.intent === 'compliance_readiness_view') {
            logRouting('HANDLED_INTERNAL', {
                handler: 'compliance_api',
                intent: result.intent,
                framework: result.frameworks?.[0] || 'Overall',
            });
            const framework = result.frameworks?.[0] || 'Overall';
            addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
            setIsOpen(true);
            updatePanelPos();

            const loadingId = (Date.now() + 1).toString();
            addMessage({ id: loadingId, text: `📊 Fetching ${framework} readiness data...`, sender: 'bot', timestamp: new Date() });

            try {
                // Try new compliance endpoint first, fall back to reporting service
                let summary;
                try {
                    const resp = await ComplianceService.getReadiness(framework !== 'Overall' ? framework : undefined);
                    summary = resp.data;
                    console.log('[AI COMMAND BAR] Readiness summary received:', summary);
                } catch {
                    const resp = await ReportingService.getFrameworkReadiness(framework);
                    summary = resp.data;
                }

                addMessage({
                    id: (Date.now() + 2).toString(),
                    text: `Here is your current **${framework}** readiness briefing.`,
                    sender: 'bot',
                    timestamp: new Date(),
                    data: {
                        intent: 'PRODUCT_READINESS_BRIEFING',
                        action_metadata: {
                            framework: summary.framework_name || summary.framework_code || framework,
                            percentage: summary.readiness_percentage,
                            ready: summary.compliant_controls ?? summary.ready_count,
                            pending: summary.in_progress_controls ?? summary.pending_count,
                            draft: summary.draft_count,
                            missing: summary.not_met_controls ?? summary.missing_count,
                            total: summary.total_controls ?? summary.total_requirements,
                            top_gaps: summary.top_gaps || []
                        }
                    }
                });
            } catch (err) {
                console.error('Error fetching framework readiness:', err);
                addMessage({ id: (Date.now() + 2).toString(), text: "I couldn't fetch the readiness data. Please check the Reports page.", sender: 'bot', timestamp: new Date() });
            }
            return true;
        }

        // ── compliance_gaps_view ──────────────────────────────────────────────
        if (result.intent === 'compliance_gaps_view') {
            logRouting('HANDLED_INTERNAL', {
                handler: 'compliance_api',
                intent: result.intent,
                framework: result.frameworks?.[0] || null,
            });
            const framework = result.frameworks?.[0] || null;
            addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
            setIsOpen(true);
            updatePanelPos();
            addMessage({ id: (Date.now() + 1).toString(), text: `🔍 Fetching compliance gaps${framework ? ` for ${framework}` : ''}...`, sender: 'bot', timestamp: new Date() });

            try {
                const resp = await ComplianceService.getGaps(framework);
                const data = resp.data;
                addMessage({
                    id: (Date.now() + 2).toString(),
                    text: data.summary.total_gaps === 0
                        ? '✅ No gaps found! All controls are compliant.'
                        : `Found **${data.summary.total_gaps}** gap${data.summary.total_gaps !== 1 ? 's' : ''} — ${data.summary.critical_gaps} critical, ${data.summary.in_plan} in plan.`,
                    sender: 'bot',
                    timestamp: new Date(),
                    data: { intent: 'COMPLIANCE_GAPS', gaps_data: data }
                });
            } catch (err) {
                console.error('Error fetching gaps:', err);
                addMessage({ id: (Date.now() + 2).toString(), text: "Couldn't fetch gap data. Visit Reports for details.", sender: 'bot', timestamp: new Date() });
            }
            return true;
        }

        // ── compliance_control_view ───────────────────────────────────────────
        if (result.intent === 'compliance_control_view') {
            logRouting('HANDLED_INTERNAL', {
                handler: 'compliance_api',
                intent: result.intent,
                control_id: result.control_ids?.[0]?.normalized || null,
                search_query: result.search_query || null,
            });
            const framework = result.frameworks?.[0] || null;
            const controlId = result.control_ids?.[0]?.normalized;
            // Extract control ID from the action params
            const apiAction = result.actions.find(a => a.path === '/compliance/controls/search');
            const searchQ = controlId || apiAction?.params?.q || apiAction?.params?.control_id || result.search_query || queryValue;

            addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
            setIsOpen(true);
            updatePanelPos();

            if (controlId) {
                addMessage({ id: (Date.now() + 1).toString(), text: `🔎 Fetching details for control ${controlId}...`, sender: 'bot', timestamp: new Date() });
                try {
                    const resp = await ComplianceService.getControl(controlId);
                    const data = resp.data;
                    addMessage({
                        id: (Date.now() + 2).toString(),
                        text: `Found details for **${data.control_title}**.`,
                        sender: 'bot',
                        timestamp: new Date(),
                        data: { intent: 'COMPLIANCE_CONTROL_DETAIL', control: data }
                    });
                } catch (err) {
                    console.error('Error fetching control detail:', err);
                    addMessage({ id: (Date.now() + 2).toString(), text: `Couldn't find an exact match for "${controlId}". Searching instead...`, sender: 'bot', timestamp: new Date() });
                    // Fallback to search
                    const resp = await ComplianceService.searchControls(controlId, framework);
                    addMessage({
                        id: (Date.now() + 3).toString(),
                        text: resp.data.count === 0 ? `No results for "${controlId}".` : `Found matches for "${controlId}".`,
                        sender: 'bot', timestamp: new Date(),
                        data: { intent: 'COMPLIANCE_CONTROL', control_data: resp.data }
                    });
                }
                return true;
            }

            addMessage({ id: (Date.now() + 1).toString(), text: `🔎 Searching controls for "${searchQ}"...`, sender: 'bot', timestamp: new Date() });

            try {
                const resp = await ComplianceService.searchControls(searchQ, framework);
                const data = resp.data;
                addMessage({
                    id: (Date.now() + 2).toString(),
                    text: data.count === 0
                        ? `No controls found matching "${searchQ}".`
                        : `Found **${data.count}** control${data.count !== 1 ? 's' : ''} matching "${searchQ}".`,
                    sender: 'bot',
                    timestamp: new Date(),
                    data: { intent: 'COMPLIANCE_CONTROL', control_data: data }
                });
            } catch (err) {
                console.error('Error fetching control:', err);
                addMessage({ id: (Date.now() + 2).toString(), text: "Couldn't retrieve control details. Check Reports for control info.", sender: 'bot', timestamp: new Date() });
            }
            return true;
        }

        // ── compliance_report_generate ────────────────────────────────────────
        if (result.intent === 'compliance_report_generate') {
            logRouting('HANDLED_INTERNAL', {
                handler: 'compliance_api',
                intent: result.intent,
                framework: result.frameworks?.[0] || null,
            });
            const framework = result.frameworks?.[0] || null;
            addMessage({ id: Date.now().toString(), text: queryValue, sender: 'user', timestamp: new Date() });
            setIsOpen(true);
            updatePanelPos();
            addMessage({ id: (Date.now() + 1).toString(), text: `📋 Generating compliance report${framework ? ` for ${framework}` : ''}...`, sender: 'bot', timestamp: new Date() });

            try {
                const resp = await ComplianceService.getReport(framework);
                const data = resp.data;
                addMessage({
                    id: (Date.now() + 2).toString(),
                    text: `**${data.framework_name}** compliance report generated.`,
                    sender: 'bot',
                    timestamp: new Date(),
                    data: { intent: 'COMPLIANCE_REPORT', report_data: data }
                });
            } catch (err) {
                console.error('Error generating report:', err);
                addMessage({ id: (Date.now() + 2).toString(), text: "Couldn't generate the report. Try visiting the Reports page directly.", sender: 'bot', timestamp: new Date() });
            }
            return true;
        }

        // ── Layer 1 handled it: execute actions directly ───────────────────────
        let navTarget = null;
        let navParams = {};

        for (const action of result.actions) {
            if (action.type === 'NAVIGATE') {
                navTarget = action.target;
                navParams = action.params ?? {};
            } else if (action.type === 'API_CALL') {
                // Simplified API call mapping for export
                if (action.path === '/api/policy/exports/pdf' && action.body?.all_approved) {
                    ExportService.exportPack({}).then((response) => {
                        showToast('Success: Exporting all approved policies.', 'success');
                    }).catch(() => {
                        showToast('Failed to export policies.', 'error');
                    });
                }
            } else if (action.type === 'OPEN_MODAL') {
                if (action.name === 'HelpPanel') {
                    onDockToHelp?.();
                }
            }
        }

        if (navTarget) {
            const routeMap = {
                'policy.list': '/documents',
                'policy.templates': '/documents',
                'policy.create_from_template': '/documents',
                'reports': '/reports',
                'compliance.readiness': '/reports',
                'compliance.gaps': '/reports',
                'compliance.report': '/reports',
                'compliance.control': '/reports',
                'audit.logs': '/settings',
                'users': '/users',
                'settings': '/settings',
                'dashboard': '/'
            };
            const routePath = routeMap[navTarget] || '/';
            logRouting('HANDLED_INTERNAL', {
                handler: 'navigation',
                intent: result.intent,
                target: navTarget,
                path: routePath,
                params: navParams,
            });

            const qs = new URLSearchParams();
            if (navParams.q) qs.set('q', String(navParams.q));
            if (navParams.framework) qs.set('framework', String(navParams.framework));
            const queryString = qs.toString() ? `?${qs.toString()}` : '';

            navigate(routePath + queryString);
            setIsOpen(false);
            setIsFocused(false);
            return true;
        }

        logRouting('HANDLED_INTERNAL', {
            handler: 'internal_nlp',
            intent: result.intent,
            actions: result.actions,
        });
        return true;
    }, [location.pathname, addMessage, setIsOpen, updatePanelPos, navigate, onDockToHelp, isAdmin, showToast, assessmentContext, logRouting]);



    /* ─── Render helpers ─────────────────────────────────── */
    const renderInlineMarkdown = (text) => {
        return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index}>{part.slice(2, -2)}</strong>;
            }
            return <React.Fragment key={index}>{part}</React.Fragment>;
        });
    };

    const renderAssistantText = (text) => {
        const blocks = normalizeAssistantMarkdown(text).split(/\n{2,}/).filter(Boolean);

        return (
            <div className={classes.formattedResponse}>
                {blocks.map((block, blockIndex) => {
                    const lines = block.split('\n').filter(Boolean);
                    const listLines = lines.filter(line => /^-\s+/.test(line));

                    if (listLines.length === lines.length) {
                        return (
                            <ul key={blockIndex} className={classes.responseList}>
                                {listLines.map((line, lineIndex) => (
                                    <li key={lineIndex}>{renderInlineMarkdown(line.replace(/^-\s+/, ''))}</li>
                                ))}
                            </ul>
                        );
                    }

                    return (
                        <div key={blockIndex} className={classes.responseParagraph}>
                            {lines.map((line, lineIndex) => (
                                <React.Fragment key={lineIndex}>
                                    {lineIndex > 0 && <br />}
                                    {renderInlineMarkdown(line)}
                                </React.Fragment>
                            ))}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderStructuredResponse = (data) => {
        const handleActionClick = (act) => {
            if (isLoading) return;
            callAI(act);
        };

        return (
            <div className={classes.structuredResponse}>
                {data.confidence && (
                    <div className={classes.confidenceBadge} data-level={data.confidence.toLowerCase()}>
                        <span className={classes.confidenceDot} />
                        {data.confidence} Confidence
                    </div>
                )}

                {/* Product Readiness Briefing */}
                {data.intent === 'PRODUCT_READINESS_BRIEFING' && data.action_metadata && (
                    <div className={classes.responseCard} data-type="info">
                        <div className={classes.cardHeader}>
                            <CheckCircle size={13} className={classes.iconAccent} />
                            <span>{data.action_metadata.framework} Readiness</span>
                        </div>
                        <div className={classes.actionContent}>
                            <div className={classes.scoreBriefing}>
                                <div className={classes.scoreCircle}>
                                    <span className={classes.scoreValue}>{Math.round(data.action_metadata.percentage || 0)}%</span>
                                    <span className={classes.scoreLabel}>Coverage</span>
                                </div>
                                <div className={classes.scoreDetails}>
                                    <div className={classes.detailRow}>
                                        <CheckCircle size={12} className={classes.iconGreen} />
                                        <span><b>✓</b> Implemented: {data.action_metadata.ready}</span>
                                    </div>
                                    <div className={classes.detailRow}>
                                        <Clock size={12} className={classes.iconBlue} />
                                        <span><b>⚠</b> Partial: {data.action_metadata.pending}</span>
                                    </div>
                                    <div className={classes.detailRow}>
                                        <Plus size={12} className={classes.iconRed} />
                                        <span><b>✗</b> Missing: {data.action_metadata.missing}</span>
                                    </div>
                                </div>
                            </div>

                            {data.action_metadata.top_gaps && data.action_metadata.top_gaps.length > 0 && (
                                <div style={{ marginTop: '0.8rem', paddingTop: '0.6rem', borderTop: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                                        Top Gaps
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                        {data.action_metadata.top_gaps.map((gap, i) => (
                                            <div key={i} style={{ fontSize: '0.72rem', color: '#1e293b', display: 'flex', gap: '0.4rem' }}>
                                                <span style={{ color: '#dc2626' }}>•</span>
                                                {gap}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Button
                                variant="secondary"
                                size="sm"
                                className={classes.actionCardBtn}
                                style={{ marginTop: '1rem' }}
                                onClick={() => handleNavigate('/reports')}
                                icon={ArrowRight}
                            >
                                View Detailed Report
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Compliance Gaps Response ── */}
                {data.intent === 'COMPLIANCE_GAPS' && data.gaps_data && (
                    <div className={classes.responseCard} data-type="warn">
                        <div className={classes.cardHeader}>
                            <AlertCircle size={13} className={classes.iconAmber} />
                            <span>Compliance Gaps</span>
                            <span style={{
                                marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 700, color: '#dc2626',
                                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px'
                            }}>
                                {data.gaps_data.summary.total_gaps} gaps
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                            {data.gaps_data.summary.critical_gaps > 0 && (
                                <span style={{
                                    fontSize: '0.65rem', fontWeight: 700, color: '#dc2626',
                                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px'
                                }}>
                                    🚨 {data.gaps_data.summary.critical_gaps} Critical
                                </span>
                            )}
                            {data.gaps_data.summary.in_plan > 0 && (
                                <span style={{
                                    fontSize: '0.65rem', fontWeight: 700, color: '#d97706',
                                    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px'
                                }}>
                                    📋 {data.gaps_data.summary.in_plan} In Plan
                                </span>
                            )}
                            {data.gaps_data.summary.not_started > 0 && (
                                <span style={{
                                    fontSize: '0.65rem', fontWeight: 700, color: '#64748b',
                                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px'
                                }}>
                                    ⏸ {data.gaps_data.summary.not_started} Not Started
                                </span>
                            )}
                        </div>
                        <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                            {(data.gaps_data.gaps || []).slice(0, 8).map((gap, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                    padding: '0.35rem 0.5rem', borderRadius: 8,
                                    background: gap.severity === 'high' ? '#fef2f2' : gap.severity === 'medium' ? '#fffbeb' : '#f8fafc',
                                    border: `1px solid ${gap.severity === 'high' ? '#fecaca' : gap.severity === 'medium' ? '#fde68a' : '#e2e8f0'}`,
                                }}>
                                    <span style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                                        {gap.severity === 'high' ? '🔴' : gap.severity === 'medium' ? '🟡' : '⚪'}
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
                                            {gap.control_title}
                                        </div>
                                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 1 }}>
                                            {gap.domain} · {gap.framework}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button variant="secondary" size="sm" className={classes.actionCardBtn}
                            style={{ marginTop: '0.75rem' }} onClick={() => handleNavigate('/reports')} icon={ArrowRight}>
                            View Full Gap Analysis
                        </Button>
                    </div>
                )}

                {/* ── Control Detail Response ── */}
                {data.intent === 'COMPLIANCE_CONTROL_DETAIL' && data.control && (
                    <div className={classes.responseCard} data-type="info">
                        <div className={classes.cardHeader}>
                            <CheckCircle size={13} className={classes.iconAccent} />
                            <span>Control Detail: {data.control.control_title.split(' ')[0]}</span>
                            <span style={{
                                marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 700,
                                color: data.control.status === 'complied' ? '#059669' : data.control.status === 'partial' ? '#d97706' : '#dc2626',
                                background: data.control.status === 'complied' ? '#f0fdf4' : data.control.status === 'partial' ? '#fffbeb' : '#fef2f2',
                                border: '1px solid currentColor', borderRadius: 6, padding: '2px 8px'
                            }}>
                                {data.control.status === 'complied' ? '✓ Compliant' : data.control.status === 'partial' ? '⚠ Partial' : '✗ Missing'}
                            </span>
                        </div>
                        <div style={{ padding: '0.2rem 0' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.4rem' }}>
                                {data.control.control_title}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.5, marginBottom: '0.8rem' }}>
                                {data.control.description}
                            </div>

                            {data.control.questions && data.control.questions.length > 0 && (
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.6rem' }}>
                                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                                        Evidence & Requirements ({data.control.questions.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                        {data.control.questions.map((q, i) => (
                                            <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                                                <span style={{ fontSize: '0.7rem', flexShrink: 0, marginTop: 2 }}>
                                                    {q.compliant === true || (q.response && q.response.toLowerCase() === 'yes') ? '✅' : q.response && q.response.toLowerCase() === 'plan' ? '🟡' : '❌'}
                                                </span>
                                                <div style={{ fontSize: '0.72rem', color: '#1e293b' }}>
                                                    {q.question}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <Button variant="secondary" size="sm" className={classes.actionCardBtn}
                            style={{ marginTop: '1rem' }} onClick={() => handleNavigate('/reports')} icon={ArrowRight}>
                            View in Compliance Manager
                        </Button>
                    </div>
                )}

                {data.intent === 'COMPLIANCE_CONTROL' && data.control_data && (
                    <div className={classes.responseCard} data-type="info">
                        <div className={classes.cardHeader}>
                            <CheckCircle size={13} className={classes.iconAccent} />
                            <span>Control Details</span>
                            <span style={{
                                marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 700, color: '#2563eb',
                                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 8px'
                            }}>
                                {data.control_data.count} result{data.control_data.count !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 200, overflowY: 'auto' }}>
                            {(data.control_data.results || []).slice(0, 5).map((ctrl, i) => (
                                <div key={i} style={{
                                    padding: '0.5rem 0.6rem', borderRadius: 8,
                                    background: '#f8fafc', border: '1px solid #e2e8f0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', justifyContent: 'space-between' }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.3, flex: 1 }}>
                                            {ctrl.control_title}
                                        </div>
                                        <span style={{
                                            fontSize: '0.6rem', fontWeight: 700, color: '#059669',
                                            background: '#f0fdf4', border: '1px solid #bbf7d0',
                                            borderRadius: 4, padding: '1px 6px', flexShrink: 0
                                        }}>
                                            {ctrl.question_count}Q
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 3 }}>
                                        {ctrl.framework && <span style={{ marginRight: 6 }}>🛡 {ctrl.framework}</span>}
                                        {ctrl.domain && <span>📂 {ctrl.domain}</span>}
                                    </div>
                                    {ctrl.description && (
                                        <div style={{
                                            fontSize: '0.68rem', color: '#475569', marginTop: 4, lineHeight: 1.4,
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                        }}>
                                            {ctrl.description}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <Button variant="secondary" size="sm" className={classes.actionCardBtn}
                            style={{ marginTop: '0.75rem' }} onClick={() => handleNavigate('/reports')} icon={ArrowRight}>
                            View in Reports
                        </Button>
                    </div>
                )}

                {/* ── Compliance Report Summary ── */}
                {data.intent === 'COMPLIANCE_REPORT' && data.report_data && (
                    <div className={classes.responseCard} data-type="info">
                        <div className={classes.cardHeader}>
                            <BarChart3 size={13} className={classes.iconAccent} />
                            <span>{data.report_data.framework_name} Report</span>
                        </div>
                        <div className={classes.scoreBriefing} style={{ marginBottom: '0.6rem' }}>
                            <div className={classes.scoreCircle}>
                                <span className={classes.scoreValue}>
                                    {Math.round(data.report_data.summary?.readiness_percentage || 0)}%
                                </span>
                                <span className={classes.scoreLabel}>Ready</span>
                            </div>
                            <div className={classes.scoreDetails}>
                                <div className={classes.detailRow}>
                                    <CheckCircle size={12} className={classes.iconGreen} />
                                    <span>{data.report_data.summary?.total_compliant} Compliant</span>
                                </div>
                                <div className={classes.detailRow}>
                                    <AlertCircle size={12} className={classes.iconAmber} />
                                    <span>{data.report_data.summary?.total_not_met} Not Met</span>
                                </div>
                                <div className={classes.detailRow}>
                                    <Clock size={12} className={classes.iconBlue} />
                                    <span>{data.report_data.summary?.total_in_plan} In Plan</span>
                                </div>
                            </div>
                        </div>
                        {(data.report_data.domains || []).slice(0, 4).map((d, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                                <span style={{
                                    fontSize: '0.65rem', color: '#64748b', flex: 1,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                }}>
                                    {d.domain_name}
                                </span>
                                <div style={{ width: 80, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
                                    <div style={{
                                        width: `${d.total_count > 0 ? Math.round((d.compliant_count / d.total_count) * 100) : 0}%`,
                                        height: '100%', background: '#10b981', borderRadius: 3,
                                    }} />
                                </div>
                                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#10b981', flexShrink: 0 }}>
                                    {d.total_count > 0 ? Math.round((d.compliant_count / d.total_count) * 100) : 0}%
                                </span>
                            </div>
                        ))}

                        <Button variant="secondary" size="sm" className={classes.actionCardBtn}
                            style={{ marginTop: '1rem' }} onClick={() => handleNavigate('/reports')} icon={ArrowRight}>
                            Open Full Report
                        </Button>
                    </div>
                )}

                {data.gaps && data.gaps.length > 0 && (
                    <div className={classes.responseCard}>
                        <div className={classes.cardHeader}>
                            <AlertCircle size={13} className={classes.iconAmber} />
                            <span>Identified Gaps</span>
                        </div>
                        <div className={classes.list}>
                            {data.gaps.map((gap, i) => (
                                <div key={i} className={classes.listItem}>
                                    <div className={classes.listDot} />
                                    {gap}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {data.suggested_quick_asks && data.suggested_quick_asks.length > 0 && (
                    <div className={classes.quickAsks}>
                        <div className={classes.quickAsksLabel}>
                            <Sparkles size={10} className={classes.iconSparkle} />
                            Suggested Follow-Ups
                        </div>
                        <div className={classes.quickAsksList}>
                            {data.suggested_quick_asks.map((ask, i) => (
                                <button
                                    key={i}
                                    className={classes.quickAskBtn}
                                    onClick={() => handleActionClick(ask)}
                                    disabled={isLoading}
                                >
                                    <span>{ask}</span>
                                    <ArrowRight size={14} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div
                ref={barRef}
                className={`${classes.aiCommandBar} ${(isOpen || isFocused) ? classes.active : ''}`}
                onClick={() => inputRef.current?.focus()}
            >
                <Sparkles
                    size={20}
                    className={`${classes.aiCommandIcon} ${isFocused || isOpen ? classes.aiCommandIconActive : ''}`}
                />

                <input
                    ref={inputRef}
                    type="text"
                    className={classes.aiCommandInput}
                    placeholder="Ask Proofyx..."
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={handleKeyDownInput}
                />



                {/* Re-open conversation pill — visible when chat closed but history exists */}
                {!isOpen && messages.length > 0 && (
                    <button
                        onClick={() => { updatePanelPos(); setIsOpen(true); }}
                        title="Re-open AI conversation"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.25rem 0.6rem 0.25rem 0.45rem',
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                            border: 'none', borderRadius: '20px',
                            color: '#fff', cursor: 'pointer',
                            fontSize: '0.68rem', fontWeight: 700,
                            lineHeight: 1, flexShrink: 0,
                            boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                            transition: 'opacity 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.04)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                        <Sparkles size={11} />
                        <span>{messages.length} msg{messages.length !== 1 ? 's' : ''}</span>
                    </button>
                )}

                {(!isOpen && value.trim()) && (
                    <button
                        className={classes.aiCommandSubmit}
                        onClick={(e) => {
                            e.stopPropagation();
                            triggerSearch();
                        }}
                    >
                        <ArrowRight size={18} />
                    </button>
                )}

                {isOpen && (
                    <button
                        className={classes.aiCommandClose}
                        onClick={(e) => {
                            e.stopPropagation();
                            closePanel();
                        }}
                    >
                        <X size={16} />
                    </button>
                )}
            </div>

            {/* Suggestions Panel (Portal) */}
            {showSuggestions && createPortal(
                <div
                    ref={suggestionRef}
                    style={{
                        position: 'fixed',
                        top: panelPos.top,
                        left: panelPos.left,
                        width: panelPos.width,
                        zIndex: 9999,
                        background: 'rgba(255,255,255,0.97)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(226,232,240,0.9)',
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px -4px rgba(0,0,0,0.10), 0 24px 48px -12px rgba(15,23,42,0.15)',
                        overflow: 'hidden',
                        animation: 'sugg-pop 0.22s cubic-bezier(0.34,1.56,0.64,1)',
                    }}
                >
                    <style>{`
                        @keyframes sugg-pop {
                            from { opacity: 0; transform: translateY(-10px) scale(0.97); }
                            to   { opacity: 1; transform: translateY(0) scale(1); }
                        }
                    `}</style>

                    {/* Header */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.875rem 1.5rem 0.75rem',
                        borderBottom: '1px solid rgba(226,232,240,0.6)',
                        background: 'linear-gradient(to right, rgba(248,250,255,0.7), rgba(241,245,255,0.5))',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Sparkles size={14} style={{ color: '#2563eb' }} />
                            <span style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#334155' }}>
                                Suggested Actions
                            </span>
                        </div>
                        <span style={{
                            fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8',
                            background: '#f1f5f9', border: '1px solid #e2e8f0',
                            borderRadius: '6px', padding: '2px 8px',
                        }}>
                            Press ↵ to ask
                        </span>
                    </div>

                    {/* Main content grid: 3 columns for groups + Sidebar for Recent */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) 240px', gap: 0, padding: '1rem 0' }}>

                        {/* Three Command Category Columns */}
                        {AI_SUGGESTIONS.map((group, gIdx) => (
                            <div key={group.category} style={{
                                padding: '0.25rem 1.25rem',
                                borderRight: gIdx < AI_SUGGESTIONS.length - 1 ? '1px solid #f1f5f9' : 'none',
                                display: 'flex', flexDirection: 'column',
                                gap: '0.75rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                                    <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{group.label.split(' ')[0]}</span>
                                    <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: group.color }}>
                                        {group.label.split(' ').slice(1).join(' ')}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                    {group.items.map((item) => {
                                        const isAI = item.hint && item.hint.includes('AI');
                                        return (
                                            <div key={item.label} style={{ position: 'relative' }} title={item.hint || ''}>
                                                <button
                                                    onClick={() => handleSuggestionClick(item.query)}
                                                    style={{
                                                        padding: '0.4rem 0.6rem',
                                                        paddingRight: isAI ? '2.4rem' : '0.6rem',
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        fontSize: '0.78rem',
                                                        fontWeight: 600,
                                                        color: '#475569',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.1s',
                                                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                                        width: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.background = group.color + '15';
                                                        e.currentTarget.style.borderColor = group.color + '40';
                                                        e.currentTarget.style.color = group.color;
                                                        e.currentTarget.style.transform = 'translateX(2px)';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.background = '#f8fafc';
                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                        e.currentTarget.style.color = '#475569';
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                    }}
                                                >
                                                    <span>{item.label}</span>
                                                    {isAI && (
                                                        <span style={{
                                                            fontSize: '0.55rem', fontWeight: 800,
                                                            color: '#7c3aed', background: '#f5f3ff',
                                                            border: '1px solid #ddd6fe',
                                                            borderRadius: 4, padding: '1px 5px',
                                                            letterSpacing: '0.05em', flexShrink: 0,
                                                        }}>AI</span>
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}


                        {/* Column 5: Recent Queries */}
                        <div style={{ padding: '0.25rem 1.25rem', minHeight: '180px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <RotateCcw size={11} style={{ color: '#0891b2' }} />
                                    <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#0891b2' }}>
                                        Recent
                                    </span>
                                </div>
                                {recentQueries.length > 0 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRecentQueries([]);
                                            localStorage.removeItem('ai_query_history');
                                        }}
                                        style={{
                                            background: 'none', border: 'none', fontSize: '0.6rem',
                                            fontWeight: 700, color: '#94a3b8', cursor: 'pointer',
                                        }}
                                    >
                                        CLEAR
                                    </button>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                {recentQueries.length === 0 ? (
                                    <p style={{ fontSize: '0.7rem', color: '#cbd5e1', fontStyle: 'italic', marginTop: '0.5rem' }}>
                                        No recent history
                                    </p>
                                ) : recentQueries.slice(0, 4).map((q, i) => (
                                    <div
                                        key={i}
                                        onClick={() => handleSuggestionClick(q)}
                                        style={{
                                            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                            padding: '0.45rem', borderRadius: '8px',
                                            cursor: 'pointer', fontSize: '0.75rem',
                                            color: '#64748b', transition: 'all 0.1s',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = '#f1f5f9';
                                            e.currentTarget.style.color = '#1e293b';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#64748b';
                                        }}
                                    >
                                        <Search size={10} style={{ color: '#cbd5e1', marginTop: '3px', flexShrink: 0 }} />
                                        <span style={{
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            lineHeight: 1.3
                                        }}>
                                            {q}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>,
                document.body
            )}

            {/* Expanded Conversation Panel (Portal) */}
            {isOpen && createPortal(
                <>
                    <div className={classes.floatingBackdrop} onClick={closePanel} />
                    <div
                        ref={panelRef}
                        className={classes.floatingPanel}
                        style={{
                            top: panelPos.top,
                            left: panelPos.left,
                            width: panelPos.width
                        }}
                    >
                        <header className={classes.panelHeader}>
                            <div className={classes.panelHeaderLeft}>
                                <div className={classes.panelAiDot} />
                                <span className={classes.panelTitle}>AI Assistant</span>
                                {isLoading && <span className={classes.panelStatus}>Thinking...</span>}
                            </div>
                            <div className={classes.panelHeaderActions}>
                                <button className={classes.panelIconBtn} onClick={closePanel}>
                                    <X size={16} />
                                </button>
                            </div>
                        </header>

                        <div className={classes.panelBody}>
                            {messages.map((msg, i) => (
                                <div
                                    key={msg.id}
                                    className={`${classes.threadMsg} ${msg.sender === 'user' ? classes.threadUser : ''}`}
                                >
                                    <div className={`${classes.threadAvatar} ${msg.sender === 'user' ? classes.avatarUser : classes.avatarBot}`}>
                                        {msg.sender === 'user' ? <User size={16} /> : <Bot size={16} />}
                                    </div>
                                    <div className={classes.threadContent}>
                                        {msg.sender === 'user' ? (
                                            <div className={classes.userBubble}>{msg.text}</div>
                                        ) : (
                                            <div className={classes.botBubble}>
                                                <div className={classes.botText}>{renderAssistantText(msg.text)}</div>
                                                {msg.data && renderStructuredResponse(msg.data)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className={classes.threadMsg}>
                                    <div className={`${classes.threadAvatar} ${classes.avatarBot}`}>
                                        <Bot size={16} />
                                    </div>
                                    <div className={classes.threadContent}>
                                        <div className={classes.typingDots}>
                                            <span /><span /><span />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} style={{ height: 1, clear: 'both' }} />
                        </div>

                        <footer className={classes.panelFooter}>
                            <input
                                type="text"
                                className={classes.followUpInput}
                                placeholder="Ask a follow-up..."
                                value={followUp}
                                onChange={(e) => setFollowUp(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFollowUp()}
                            />
                            <Button
                                variant="primary"
                                className={classes.followUpSendOverride}
                                onClick={handleFollowUp}
                                disabled={!followUp.trim() || isLoading}
                                icon={ArrowRight}
                            />
                        </footer>
                    </div>
                </>,
                document.body
            )}
        </>
    );
};
